<template>
  <div>
    <div class="key is-flex">
      <div class="key-item is-flex">
        <div class="key-item-color has-background-success"></div>Positive
      </div>
      <div class="key-item is-flex">
        <div class="key-item-color has-background-info"></div>Neutral
      </div>
      <div class="key-item is-flex">
        <div class="key-item-color has-background-danger"></div>Negative
      </div>
      <div class="key-item is-flex">
        <div class="key-item-color has-background-grey-lighter"></div>No entry
      </div>
    </div>
    <div v-for="[year, months] in daysByYearAndMonth" v-bind:key="year">
      <div class="year fancy is-size-4">{{ year }}</div>
      <svg class="month" v-for="[month, days] in months" v-bind:key="month"
        :width="monthWidth" :height="(cellSize * 7) + (cellMargin * 6)">
        <g>
          <text class="month-name" :x="((cellSize * 7) + (cellMargin * 8)) / 2" y="15"
            text-anchor="middle">{{ month }}</text>
          <rect v-for="dayData in days" class="day" :width="cellSize" :height="cellSize"
            v-bind:key="dayData.date.getDate()" rx="3" ry="3" :fill="dayData.fill"
            :x="(day(dayData.date) * cellSize) + (day(dayData.date) * cellMargin) + cellMargin"
            :y="dayData.y"
          >
            <title>{{ dayData.title }}</title>
          </rect>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
import * as d3 from '@/d3'

import { POSITIVE_THRESHOLD, NEGATIVE_THRESHOLD } from '@/util'

export default {
  name: 'HeatMap',
  props: ['lines'],
  data() {
    return {
      cellMargin: 2,
      day: d3.timeFormat('%w'),
      monthName: d3.timeFormat('%B'),
      week: d3.timeFormat('%U'),
      positiveFill: '#23d160',
      neutralFill: '#209cee',
      negativeFill: '#ff3860'
    }
  },
  computed: {
    cellSize() {
      if (this.$mq.isMobile()) {
        return ((window.innerWidth - 40) / 7) - this.cellMargin
      }

      return 20
    },
    maxDate() {
      return d3.max(this.lines, this.getCreatedAt)
    },
    minDate() {
      return d3.min(this.lines, this.getCreatedAt)
    },
    daysByYearAndMonth() {
      const days = d3.timeDay.range(d3.timeMonth.floor(this.minDate),
        d3.timeMonth.ceil(this.maxDate))
        .map((date) => {
          const line = this.linesObject[date.toDateString()]
          return {
            ...line,
            date,
            fill: this.fill(line),
            title: this.title(line),
            y: this.y(date)
          }
        })

      const daysByYearAndMonth = d3.groups(days, ({ date }) => date.getFullYear(),
        ({ date }) => this.monthName(date))
        .reverse()

      daysByYearAndMonth.forEach(([, months]) => months.reverse())

      return daysByYearAndMonth
    },
    monthWidth() {
      return (this.cellSize * 7) + (this.cellMargin * 8)
    },
    linesObject() {
      return this.lines.reduce((acc, curr) => (
        { ...acc, [this.getCreatedAt(curr).toDateString()]: curr }
      ), {})
    }
  },
  methods: {
    fill(line) {
      if (line) {
        const { sentimentScore } = line

        if (sentimentScore > POSITIVE_THRESHOLD) {
          return this.positiveFill
        }

        if (sentimentScore < NEGATIVE_THRESHOLD) {
          return this.negativeFill
        }

        return this.neutralFill
      }

      return '#f5f5f5'
    },
    getCreatedAt(line) {
      return line.createdAt.toDate ? line.createdAt.toDate() : line.createdAt
    },
    title(line) {
      if (line) {
        return line.text
      }

      return null
    },
    y(date) {
      const weekDiff = this.week(date)
        - this.week(new Date(date.getFullYear(), date.getMonth(), 1))
      return weekDiff * this.cellSize
        + weekDiff * this.cellMargin
        + this.cellMargin + 20
    }
  }
};
</script>

<style scoped lang="scss">
.year {
  padding-bottom: 15px;
  padding-top: 20px;
}

.month {
  margin-right: 8px;
}

.key {
  flex-wrap: wrap;

  .key-item {
    margin-right: 8px;

    .key-item-color {
      border-radius: 3px;
      height: 20px;
      margin-right: 5px;
      width: 20px;
    }
  }
}

</style>
